import { Button, Group, Stack } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, TextSerif } from "@/components/elements";
import { useGarageCars } from "@/services/cars/get-garage-cars";

import type { FC } from "react";

export type CarComparatorProps = {
  defaultCar1Id?: string;
  defaultCar2Id?: string;
  onCancel?: () => void;
  onCompare: (car1Id: string, car2Id: string) => void;
  garageId: string | undefined;
  carsData?: { label: string; value: string }[];
};

export const CarComparator: FC<CarComparatorProps> = ({
  defaultCar1Id,
  defaultCar2Id,
  onCancel,
  onCompare,
  garageId,
  carsData,
}) => {
  const { t } = useTranslation();
  const [car1Id, setCar1Id] = useState<string | null | undefined>(defaultCar1Id || null);
  const [car2Id, setCar2Id] = useState<string | null | undefined>(defaultCar2Id || null);

  const { data: carsInGarage } = useGarageCars({ garageId });
  const data =
    carsData ||
    (carsInGarage &&
      carsInGarage.results &&
      carsInGarage.results?.map((car) => ({
        label: `${car.make} ${car.model}${car.nickname ? ` (${car.nickname})` : ""}`,
        value: car.identifier,
      }))) ||
    [];

  function handleSubmit() {
    if (car1Id && car2Id) {
      onCompare(car1Id, car2Id);
    }
  }

  if (!garageId && (!carsData || carsData.length === 0)) {
    return null;
  }

  return (
    <Stack>
      <TextSerif>{t("compare.comparator.title")}</TextSerif>
      <Dropdown
        data={data}
        value={car1Id}
        onChange={setCar1Id}
        label={t("compare.comparator.label1")}
        placeholder={t("compare.comparator.placeholder")}
      />
      <Dropdown
        data={data}
        value={car2Id}
        onChange={setCar2Id}
        label={t("compare.comparator.label2")}
        placeholder={t("compare.comparator.placeholder")}
      />
      <Group gap="md" pt="md">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} flex={1} aria-label={t("common.cancel")}>
            {t("common.cancel")}
          </Button>
        )}
        <Button
          disabled={!car1Id || !car2Id}
          onClick={handleSubmit}
          flex={1}
          aria-label={t("compare.comparator.submit")}
        >
          {t("compare.comparator.submit")}
        </Button>
      </Group>
    </Stack>
  );
};
